import { FormEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, X } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';

// Allow letters (incl. accents), digits, spaces, @, dot, hyphen, underscore.
// Mirrors the validation enforced server-side in profile.controller.ts and product-user.controller.ts.
const ALLOWED_CHARS_REGEX = /^[\p{L}\p{N}\s@._-]*$/u;

export interface UsersSearchBarProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: (term: string) => void;
  onClear: () => void;
  isSearching?: boolean;
  isActive?: boolean;
  className?: string;
}

export function UsersSearchBar({
  value,
  onChange,
  onSubmit,
  onClear,
  isSearching = false,
  isActive = false,
  className
}: UsersSearchBarProps) {
  const { t } = useTranslation();

  const handleInputChange = (next: string) => {
    if (!ALLOWED_CHARS_REGEX.test(next)) {
      // Drop disallowed chars instead of erroring on every keystroke.
      const cleaned = Array.from(next).filter(ch => ALLOWED_CHARS_REGEX.test(ch)).join('');
      onChange(cleaned);
      return;
    }
    onChange(next);
  };

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  const trimmed = value.trim();
  const submitDisabled = trimmed.length === 0 || isSearching;

  return (
    <form onSubmit={handleSubmit} className={`flex w-full items-center gap-2 ${className ?? ''}`} role="search">
      <div className="relative flex-1">
        <Input
          type="search"
          value={value}
          maxLength={100}
          placeholder={t('settings.users.search.placeholder')}
          onChange={(event) => handleInputChange(event.target.value)}
          onKeyDown={handleKeyDown}
          aria-label={t('settings.users.search.placeholder')}
        />
        {isActive && !isSearching && trimmed.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            aria-label={t('settings.users.search.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="submit" size="icon" disabled={submitDisabled} aria-label={t('settings.users.search.tooltip')}>
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('settings.users.search.tooltip')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </form>
  );
}
